import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { ClipboardCheck, Loader2, Save, ShieldOff } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { catalogApi, type ApprovalWorkflow } from "@/lib/api/catalog";
import { CATALOG_APPROVAL_OPTIONS } from "./catalog-approval-options";
import { CATALOG_SETTINGS_QUERY_KEY, useCatalogSettings } from "@/hooks/useCatalogSettings";
import { useAccessControl } from "@/hooks/useAccessControl";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { cn } from "@/lib/utils";

export default function CatalogApprovalSetupPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isTenantAdmin } = useAccessControl();
  const { data: catalogSettings, isLoading } = useCatalogSettings();
  const [workflow, setWorkflow] = useState<ApprovalWorkflow>("disabled");
  const [saved, setSaved] = useState<ApprovalWorkflow>("disabled");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!catalogSettings) return;
    const current = catalogSettings.approvalWorkflow ?? "disabled";
    setWorkflow(current);
    setSaved(current);
  }, [catalogSettings]);

  const save = async () => {
    setSaving(true);
    try {
      await catalogApi.updateCatalogSettings({ approvalWorkflow: workflow });
      setSaved(workflow);
      queryClient.invalidateQueries(CATALOG_SETTINGS_QUERY_KEY);
      toast.success("Catalog approval settings saved.");
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Could not save catalog approval settings."));
    } finally {
      setSaving(false);
    }
  };

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex min-h-screen items-center justify-center bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <ShieldOff className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Administrator access required</h2>
            <p className="mt-2 text-sm text-gray-600">
              You need administrator permissions to manage catalog approval.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer>
        <PageHeader
          title="Catalog approval"
          description="Choose whether new catalog records publish immediately or wait for a reviewer."
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Catalog approval" }]}
          showSearch={false}
          titleSize="md"
        />

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-6">
            <Card className="bg-white p-6">
              <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-gray-900">
                <ClipboardCheck className="h-5 w-5 text-primary" />
                Approval workflow
              </h2>
              <p className="mb-6 text-sm text-gray-600">
                Reviewers use the catalog review queue to approve a record onto the catalog, return
                it to My Drafts, or reject it.
              </p>
              <div className="space-y-3">
                {CATALOG_APPROVAL_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    htmlFor={`workflow-${option.value}`}
                    className={cn(
                      "flex cursor-pointer items-start gap-4 rounded-lg border-2 p-4 transition-colors duration-200",
                      workflow === option.value
                        ? "border-primary bg-primary/5"
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    )}
                  >
                    <input
                      type="radio"
                      id={`workflow-${option.value}`}
                      name="approval-workflow"
                      value={option.value}
                      checked={workflow === option.value}
                      onChange={() => setWorkflow(option.value)}
                      className="mt-1 h-4 w-4 border-gray-300 text-primary focus:ring-primary"
                    />
                    <span>
                      <span className="block font-semibold text-gray-900">{option.title}</span>
                      <span className="block text-sm text-gray-600">{option.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </Card>

            <div className="flex items-center justify-between border-t border-gray-200 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate("/setup")}>
                Cancel
              </Button>
              <Button type="button" onClick={save} disabled={saving || workflow === saved}>
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save settings
              </Button>
            </div>
          </div>
        )}
      </PageContainer>
    </AppLayout>
  );
}
